/**
 * Node Renderers
 *
 * Exports all node renderers and sets up the renderer registry.
 * Import this module to register all built-in renderers.
 */

import { rendererRegistry } from '../renderer';

// Export renderer classes
export { DefaultRenderer } from './default';
export { MarkdownRenderer } from './markdown';
export { TopicRenderer } from './topic';
export { TextRenderer } from './text';
export { ImageRenderer } from './image';
export type { ImageNodeData } from './image';
export { ObjectRenderer } from './object';
export {
	classifyField,
	renderFieldValue,
	isMarkdown,
	isImageValue,
	isUrl,
	isEditableFieldType,
} from './object-fields';
export type { FieldType, ClassifiedField } from './object-fields';
export {
	HeaderRenderer,
	computeHeaderFontSize,
	AUTO_HEADER_FONT_SIZE,
} from './header';
export {
	StickyNoteRenderer,
	computeStickyNoteFontSize,
	MIN_STICKY_FONT_SIZE,
} from './sticky-note';
export { HtmlRenderer } from './html';
export { PdfRenderer } from './pdf';
export type { PdfNodeData } from './pdf';
export { FileRenderer } from './file';
export type { FileNodeData } from './file';

// Import for registration
import { DefaultRenderer } from './default';
import { MarkdownRenderer } from './markdown';
import { TopicRenderer } from './topic';
import { TextRenderer } from './text';
import { ImageRenderer } from './image';
import { ObjectRenderer } from './object';
import { HeaderRenderer } from './header';
import { StickyNoteRenderer } from './sticky-note';
import { HtmlRenderer } from './html';
import { PdfRenderer } from './pdf';
import { FileRenderer } from './file';

/**
 * Register all built-in renderers with the registry.
 * Call this once during canvas initialization.
 */
export function registerBuiltinRenderers(): void {
	// Set up object renderer as default (unknown types get card rendering)
	const objectRenderer = new ObjectRenderer();
	rendererRegistry.setDefault(objectRenderer);
	rendererRegistry.register('object', objectRenderer);

	// Register type-specific renderers
	rendererRegistry.register('markdown', new MarkdownRenderer());
	rendererRegistry.register('topic', new TopicRenderer());
	rendererRegistry.register('text', new TextRenderer());
	rendererRegistry.register('image', new ImageRenderer());
	rendererRegistry.register('header', new HeaderRenderer());
	rendererRegistry.register('sticky-note', new StickyNoteRenderer());
	rendererRegistry.register('html', new HtmlRenderer());
	rendererRegistry.register('pdf', new PdfRenderer());
	rendererRegistry.register('file', new FileRenderer());

	// Also register 'default' type explicitly
	const defaultRenderer = new DefaultRenderer();
	rendererRegistry.register('default', defaultRenderer);
}

/**
 * Check if renderers have been registered.
 */
export function renderersRegistered(): boolean {
	return rendererRegistry.hasRegistered('markdown');
}

/**
 * Check if all built-in renderers (including object, header, sticky-note)
 * have been registered.
 */
export function builtinRenderersRegistered(): boolean {
	return (
		rendererRegistry.has('object') &&
		rendererRegistry.has('header') &&
		rendererRegistry.has('sticky-note')
	);
}
